/**
 * 素材 @メンション (docs/features/material-at-mention-plan.md 案C) の純関数群。
 *
 * バッジ文字の払い出し (mentionBadgeLabel) とバッジ Map の構築は、選択素材エリア
 * (SelectedAssetsPanel・P1) とプロンプト入力欄のオートコンプリート
 * (MaterialMentionPopup・P2) の両方が使う。ポップアップが表示するバッジ文字と
 * sticky パネルのバッジ文字が食い違うと挿入直後の表示が矛盾するため、
 * 払い出しロジックはここに一本化する (SelectedAssetsPanel は本ファイルの
 * buildMentionBadgeMap を使うようリファクタ済み)。
 *
 * トークン正規化・生成前警告 (collectUnresolvedMentions) は他の生成導線
 * (シーン単位・ストーリーボード・範囲生成。P3 以降) からも呼びやすいよう、
 * UI コンポーネントに埋め込まずここへ切り出す。
 */
import type { CastingActor, GenerationAsset, SeedancePresetActorItem } from './types';
import { isMentionableReferenceAsset, isReferenceImageCandidate } from './assetMediaUtils';
import { isCharacterActor } from '../../../features/actors/actorAttributes';

// @メンションバッジの文字払い出し (0=A, 1=B, ..., 25=Z, 26=AA, 27=AB, ...)。
// スプレッドシートの列名と同じ base-26 方式。
export function mentionBadgeLabel(index: number): string {
  let n = index + 1;
  let label = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    label = String.fromCharCode(65 + rem) + label;
    n = Math.floor((n - 1) / 26);
  }
  return label;
}

/** 素材 ID (UUID) → トークンの ID 部 (ハイフン除去・小文字・先頭4桁)。 */
export function mentionIdHex4(assetId: string): string {
  return assetId.replace(/-/g, '').toLowerCase().slice(0, 4);
}

/**
 * AI俳優 (casting / seedance-preset) の @メンション候補ソース。俳優は参照上限の対象外
 * (常に送信される) なので、素材のような filterAssetsWithinReferenceLimit は通さず
 * 常に候補になる (docs/features/material-at-mention-plan.md「俳優メンション」節)。
 */
export type MentionActorSource = {
  id: string;
  kind: 'casting' | 'seedance-preset';
  displayName: string;
  /**
   * シナリオ上の役柄名 (generation_metadata.cast_roles。applyCastRolesToMentionActorSources
   * で付く)。あればチップのホバー名・候補名に俳優の表示名の代わりに使う
   * (docs/features/scenario-cast-role-names-plan.md)。
   */
  roleName?: string;
  imageUrl?: string | null;
  /**
   * casting のみ意味を持つ: character_sheet_public_url (CS) の有無。
   * CS が無いと buildVideoPayload / handleGenerateSceneVideo が reference_images へ
   * 挿入する対象を持たず、バックエンドの位置ベース解決 (resolve_actor_character_sheet_url_by_id)
   * が失敗するため、@メンション候補ゲート (isActorMentionCandidate) がこれを見て除外する
   * (docs/features/material-at-mention-plan.md レビュー指摘 P1)。
   */
  hasCharacterSheet?: boolean;
  /**
   * casting のみ意味を持つ: キャラクター俳優 (ethnicity='character') か。
   * キャラクター俳優は非写実スタイル・Seedance のいずれでも CS が送信される
   * (AI俳優キャラクター対応 P4/P5・docs/features/actor-character-support-plan.md)。
   * 人間俳優はスタイライズ系スタイル・Seedance では CS が送信されないため、
   * @メンション候補ゲート (isActorMentionCandidate) がこれを見て分岐する。
   */
  isCharacterActor?: boolean;
};

/**
 * casting / seedance-preset の「フルキャスト」情報から MentionActorSource[] を組み立てる。
 * SelectedAssetsPanel の actorItems 構築 (`casting ? [...] : seedancePreset ? [...] : []`。
 * casting と seedancePreset は排他) と同じ順序規則を再現する。呼び出し側
 * (ScenarioGenerator / SceneDetailColumn) が同じ選択 state からこの関数経由で候補を
 * 作ることで、SelectedAssetsPanel 側のバッジ (actorItems から直接組み立て) と
 * payout が一致する。
 */
export function buildMentionActorSources(params: {
  casting?: { id: string; display_name: string; face_image_public_url?: string; character_sheet_public_url?: string; ethnicity?: string | null } | null;
  additionalCastings?: CastingActor[];
  seedancePreset?: { id: string; display_name: string; thumbnail_public_url?: string | null } | null;
  additionalSeedancePresets?: SeedancePresetActorItem[];
}): MentionActorSource[] {
  const { casting, additionalCastings = [], seedancePreset, additionalSeedancePresets = [] } = params;
  if (casting) {
    return [casting, ...additionalCastings].map((c) => ({
      id: c.id,
      kind: 'casting' as const,
      displayName: c.display_name,
      imageUrl: c.face_image_public_url ?? null,
      hasCharacterSheet: typeof c.character_sheet_public_url === 'string' && !!c.character_sheet_public_url,
      isCharacterActor: isCharacterActor(c),
    }));
  }
  if (seedancePreset) {
    return [seedancePreset, ...additionalSeedancePresets].map((a) => ({
      id: a.id,
      kind: 'seedance-preset' as const,
      displayName: a.display_name,
      imageUrl: a.thumbnail_public_url ?? null,
    }));
  }
  return [];
}

/**
 * 俳優 @メンション候補ゲート (docs/features/material-at-mention-plan.md レビュー指摘 P1) の
 * 判定コンテキスト。buildVideoPayload / handleGenerateSceneVideo (useVideoHandlers.ts) が
 * 俳優のキャラクターシート (CS) / preset を reference_images or settings へ実際に含める
 * 条件 (isSeedanceModel / isStylized / includeCastingSheet の分岐) と同一のものを渡す。
 */
export type ActorMentionGateContext = {
  /** 選択中の動画モデルが Seedance ファミリか。 */
  isSeedanceModel: boolean;
  /** アニメ系スタイル (スタイライズ) か。true の場合 CS/preset とも送信されない。 */
  isStylized: boolean;
  /** 「キャラクターシートを含める」トグル (既定 true)。casting の人間俳優のみ影響する
   *  (キャラクター俳優の CS はトグルの対象外。計画書 修正3-b)。 */
  includeCastingSheet: boolean;
};

/**
 * 俳優 1 件が @メンション候補になり得るかを静的条件 (参照上限による切り詰めは含まない。
 * 下記注記) で判定する。ScenarioGenerator (ポップアップ候補・mentionActorSources) と
 * SelectedAssetsPanel (バッジ) が同じ関数を使うことで、両者の食い違いを防ぐ。
 *
 * - casting (CS 方式・人間俳優): isStylized / モデルが Seedance / includeCastingSheet===false /
 *   CS URL 未解決のいずれかで、CS が reference_images へ挿入されずバックエンドで
 *   解決できないため除外する。
 * - casting (CS 方式・キャラクター俳優 ethnicity='character'): isStylized・Seedance の
 *   いずれでも CS が送信される (AI俳優キャラクター対応 P4/P5・
 *   docs/features/actor-character-support-plan.md)。includeCastingSheet トグルは
 *   キャラクター俳優の CS には適用しない (docs/features/
 *   character-casting-consistency-plan.md 修正3-b。useVideoHandlers.ts の
 *   filterCastSheetEntriesByToggle と同じ判定)。CS URL 未解決の場合のみ人間俳優と
 *   同様に除外する。
 * - seedance-preset: asset_id で別送するため参照上限・includeCastingSheet の影響を
 *   受けないが、isStylized では送信されない・モデルが Seedance のときのみ有効。
 *
 * 注記: 参照上限超過によるキャラクターシートの切り捨てまではここでは再現しない
 * (静的条件のみのゲート)。切り捨ては送信直前にしか確定しないため、実際に送信される
 * 俳優 ID を持つ生成前警告 (collectUnresolvedMentionsForMaterialIds) 側で拾う設計上の
 * 妥協 (docs/features/material-at-mention-plan.md レビュー指摘 P1 に明記)。
 */
export function isActorMentionCandidate(
  actor: MentionActorSource,
  ctx: ActorMentionGateContext,
): boolean {
  if (actor.kind === 'seedance-preset') {
    // プリセット (BytePlus) は写実前提のため非写実スタイルでは送信されない。
    if (ctx.isStylized) return false;
    return ctx.isSeedanceModel;
  }
  // casting (CS 方式)。キャラクター俳優は isStylized / isSeedanceModel の制限を受けない。
  if (ctx.isStylized && !actor.isCharacterActor) return false;
  if (ctx.isSeedanceModel && !actor.isCharacterActor) return false;
  // includeCastingSheet トグルはキャラクター俳優の CS には適用しない (計画書 修正3-b)。
  if (!actor.isCharacterActor && !ctx.includeCastingSheet) return false;
  return !!actor.hasCharacterSheet;
}

/** actors のうち isActorMentionCandidate を満たすものだけを返す。 */
export function filterMentionActorSources(
  actors: MentionActorSource[],
  ctx: ActorMentionGateContext,
): MentionActorSource[] {
  return actors.filter((actor) => isActorMentionCandidate(actor, ctx));
}

/**
 * @メンション候補計算用の参照上限コンテキストを構築する (単一情報源)。
 * ScenarioGenerator と SelectedAssetsPanel の両方がこれを使うことで、
 * 素材絞り込み・俳優切り捨て・バッジの判定条件が食い違わないようにする。
 *
 * レビュー指摘 (2026-08-14) の2点をここで吸収する:
 * - actorCount は「選択中の全俳優数」ではなく、**静的ゲート通過後の俳優数**。
 *   ゲートで落ちた俳優 (includeCastingSheet=false / スタイライズ / CS なし /
 *   モデル種別不一致) はペイロードの参照枠を消費しない。ゲートを通った俳優は
 *   casting (CS が reference_images に入る) も seedance-preset (reference_images には
 *   入らないが effectiveRefLimit の減算で総参照枠を予約する) も枠を消費する。
 * - effectiveActorPos は selectedAssets.length ではなく、実ペイロードと同じ
 *   **画像参照列 (isReferenceImageCandidate で絞った後) の件数**で正規化する
 *   (例: 「参照動画→画像A→画像B」で castingPosition=3 なら min(3, 2)=2)。
 */
export function buildMentionReferenceLimitContext(params: {
  assets: GenerationAsset[];
  /** filterMentionActorSources (静的ゲート) 通過後の俳優一覧。 */
  gatedActors: MentionActorSource[];
  castingPosition?: number;
  disabledFromIndex?: number;
  disabledAssetIds?: string[];
}): ReferenceLimitContext & { actorCount: number; effectiveActorPos: number } {
  const referenceImageCount = params.assets.filter(isReferenceImageCandidate).length;
  // 参照枠を予約する俳優数 = 静的ゲート通過後の全俳優。casting は reference_images に
  // CS が挿入されて枠を消費し、seedance-preset は reference_images に入らないものの
  // ペイロード構築が `effectiveRefLimit = referenceImageLimit - プリセット人数` で
  // プロバイダの総参照枠を予約する (useVideoHandlers.ts) ため、どちらも数える
  // (2026-08-14 レビュー指摘: preset を数えないと上限3+preset1人で画像3枚目が
  // 候補に残るのに実際は送信されない)。
  const actorCount = params.gatedActors.length;
  const effectiveActorPos = Math.min(params.castingPosition ?? 0, referenceImageCount);
  return {
    disabledFromIndex: params.disabledFromIndex,
    disabledAssetIds: params.disabledAssetIds,
    actorCount,
    effectiveActorPos,
  };
}

/**
 * 俳優 @メンション候補のうち、参照画像上限で実際には送信されない CS 方式 (casting)
 * 俳優を除く (レビュー指摘: 上限3で「画像A→B→C→俳優」の並びだと俳優の CS は
 * 最終参照配列から切り捨てられるのに、候補には残って挿入できてしまう)。
 *
 * 俳優 i (casting のみの連番) の統合インデックスは `effectiveActorPos + i`
 * (referenceCombinedIndex が素材側を俳優人数ぶん後ろへずらすのと同じモデル)。
 * seedance-preset は asset_id 別送で参照上限を消費しないため対象外 (常に残す)。
 * isActorMentionCandidate (静的ゲート) の後段に適用する想定。
 */
export function filterActorSourcesWithinReferenceLimit(
  actors: MentionActorSource[],
  ctx: ReferenceLimitContext & { effectiveActorPos?: number },
): MentionActorSource[] {
  const effectiveActorPos = ctx.effectiveActorPos ?? 0;
  let castingIdx = -1;
  return actors.filter((actor) => {
    if (actor.kind !== 'casting') return true;
    castingIdx += 1;
    return !isReferenceIndexDisabled(effectiveActorPos + castingIdx, actor.id, ctx);
  });
}

/**
 * @メンション候補となる素材 (isMentionableReferenceAsset を満たすもの) に、
 * 表示順でバッジ文字 (A, B, C…) を払い出す。P1 (SelectedAssetsPanel の参照バッジ)
 * と P2 (ポップアップ) はこの Map を共有し、同じ素材には必ず同じバッジ文字を出す。
 *
 * `actors` を渡すと、俳優 → 素材 の順で 1 本のバッジシーケンスにする
 * (docs/features/material-at-mention-plan.md「俳優メンション」節)。俳優は
 * isActorMentionCandidate (静的ゲート) と filterActorSourcesWithinReferenceLimit
 * (CS 方式の参照上限切り捨て) を**呼び出し側で適用してから**渡すこと。
 */
export function buildMentionBadgeMap(
  assets: GenerationAsset[],
  actors: MentionActorSource[] = [],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const candidate of buildMentionCandidates(assets, actors)) {
    map.set(candidate.target.id, candidate.badge);
  }
  return map;
}

/**
 * SelectedAssetsPanel の参照上限グレーアウト (disabledFromIndex / disabledAssetIds・
 * 俳優のキャラクターシート挿入分の枠消費を含む) を表す文脈。バッジ払い出し・
 * @メンション候補の絞り込みは、この「実際には送信されない素材」判定と一致させる
 * (docs/features/material-at-mention-plan.md レビュー指摘 P1)。
 */
export type ReferenceLimitContext = {
  /** このインデックス (combinedIdx) 以降は参照上限外 (SelectedAssetsPanel と同じ意味)。 */
  disabledFromIndex?: number;
  /** インデックスに依らず個別に除外する素材 id (静止画タブの動画/音声等)。 */
  disabledAssetIds?: string[];
};

/**
 * combinedIdx (SelectedAssetsPanel の combinedItems 上の位置) と素材 id から、
 * その素材が参照上限で無効化されるかを判定する。SelectedAssetsPanel の isDisabled
 * (行 ~722) と同一条件で、二重管理を避けるためここへ切り出している。
 */
export function isReferenceIndexDisabled(
  combinedIdx: number,
  assetId: string | undefined,
  ctx: ReferenceLimitContext,
): boolean {
  if (ctx.disabledFromIndex !== undefined && combinedIdx >= ctx.disabledFromIndex) return true;
  if (assetId && ctx.disabledAssetIds?.includes(assetId)) return true;
  return false;
}

/**
 * 俳優 (casting / seedance-preset) のキャラクターシート挿入位置を考慮した、
 * assets[assetIdx] の「combinedItems 上の実効インデックス」。SelectedAssetsPanel の
 * combinedItems 構築 (actorItems を effectiveActorPos から連続挿入) と同じ規則:
 * effectiveActorPos 以降の素材は俳優の人数ぶんインデックスが後ろへずれる。
 */
export function referenceCombinedIndex(
  assetIdx: number,
  actorCount: number,
  effectiveActorPos: number,
): number {
  return assetIdx + (actorCount > 0 && assetIdx >= effectiveActorPos ? actorCount : 0);
}

/**
 * assets のうち、参照上限 (disabledFromIndex / disabledAssetIds、俳優のシート挿入も
 * 加味) で実際には送信されない素材を除いた配列を返す。SelectedAssetsPanel のバッジ
 * 表示・MaterialMentionPopup / シーン説明欄の @メンション候補が、この「実際に送信
 * される素材」集合を共有するための単一情報源 (docs/features/material-at-mention-plan.md
 * レビュー指摘 P1)。
 *
 * レビュー指摘 P2: 実ペイロード (buildVideoPayload の items) は動画・音声を除いた
 * 「画像参照列」を作ってから上限で切り詰める。disabledFromIndex や俳優の挿入位置
 * (effectiveActorPos) は元の assets 配列上の位置ではなく、この画像参照列上の位置を
 * 基準にしているため、上限判定・挿入位置補正の前に isReferenceImageCandidate
 * (buildVideoPayload の items 収集条件と同一) で絞り込み、絞り込み後のインデックスで
 * 判定する。そうしないと、動画・音声が選択に混在するとき (例: 上限2で「参照動画→
 * 画像A→画像B」) 実際には送信される画像が UI 上は上限外扱いになってしまう。
 */
export function filterAssetsWithinReferenceLimit<T extends GenerationAsset>(
  assets: T[],
  ctx: ReferenceLimitContext & { actorCount?: number; effectiveActorPos?: number },
): T[] {
  const actorCount = ctx.actorCount ?? 0;
  const effectiveActorPos = ctx.effectiveActorPos ?? 0;
  let referenceIdx = -1;
  return assets.filter((asset) => {
    if (!isReferenceImageCandidate(asset)) return false;
    referenceIdx += 1;
    const combinedIdx = referenceCombinedIndex(referenceIdx, actorCount, effectiveActorPos);
    return !isReferenceIndexDisabled(combinedIdx, asset.id, ctx);
  });
}

/** @メンション候補行 (ポップアップ・SelectedAssetsPanel バッジ) の表示に必要な最小限の形。
 *  素材は GenerationAsset をそのまま、俳優は MentionActorSource から変換する。 */
export type MentionCandidateTarget = {
  id: string;
  thumbnailUrl?: string | null;
  name?: string | null;
};

/**
 * 他シーンの動画フレーム候補 (kind='scene_frame')の表示用情報
 * (docs/features/scene-video-frame-materials-visibility-plan.md §2.2)。
 */
export type SceneFrameMentionInfo = {
  /** 挿入時の参照元シーン。 */
  sceneId: string;
  /** 挿入時のシーン番号 (ラベル固定。並べ替え後も変えない)。 */
  sceneOrder: number;
  position: 'first' | 'last';
  /** 抽出元の動画素材 (選んだテイク)。 */
  videoMaterialId: string;
  /** 参照元シーンの現在の動画が選んだテイクと違う (再生成・テイク選び直し・シーン削除)。解決はそのまま。 */
  stale: boolean;
  /** フレーム素材が削除済みで参照先が無い。 */
  missing: boolean;
};

/** @メンションポップアップの候補行 1件分。 */
export type MentionCandidate = {
  /**
   * 'actor' = casting/seedance-preset (常に候補) / 'asset' = 参照素材 /
   * 'scene_frame' = 他シーンの動画フレーム (generation_metadata.scene_frame_mentionsの記録)。
   */
  kind: 'actor' | 'asset' | 'scene_frame';
  target: MentionCandidateTarget;
  /** 挿入時点の表示順バッジ文字 (表示専用。解決には使わない)。 */
  badge: string;
  /** トークンの ID 部と同じ形式 (ハイフン除去・小文字・先頭4桁)。解決の正本。 */
  idHex4: string;
  /** kind='scene_frame'のときだけ持つ。 */
  sceneFrame?: SceneFrameMentionInfo;
};

/**
 * 俳優 (casting/seedance-preset。常に候補) + 素材 (isMentionableReferenceAsset を
 * 満たすもの。呼び出し側で参照上限内に絞り込み済みの前提) から @メンション候補一覧を
 * 構築する。俳優→素材の順で 1 本のバッジシーケンス (A, B, C…) を払い出す
 * (docs/features/material-at-mention-plan.md「俳優メンション」節)。
 */
export function buildMentionCandidates(
  assets: GenerationAsset[],
  actors: MentionActorSource[] = [],
  sceneFrames: MentionCandidate[] = [],
): MentionCandidate[] {
  const candidates: MentionCandidate[] = [];
  let idx = 0;
  for (const actor of actors) {
    candidates.push({
      kind: 'actor',
      // 役柄名があればそれを名前にする (俳優の表示名はキャスト選択用で、シナリオ以降は出さない)
      target: { id: actor.id, thumbnailUrl: actor.imageUrl, name: actor.roleName || actor.displayName },
      badge: mentionBadgeLabel(idx),
      idHex4: mentionIdHex4(actor.id),
    });
    idx += 1;
  }
  for (const asset of assets) {
    if (!isMentionableReferenceAsset(asset)) continue;
    candidates.push({
      kind: 'asset',
      target: { id: asset.id, thumbnailUrl: asset.thumbnailUrl, name: asset.name },
      badge: mentionBadgeLabel(idx),
      idHex4: mentionIdHex4(asset.id),
    });
    idx += 1;
  }
  // 他シーンの動画フレーム (記録由来)は俳優→素材の後ろに続ける。同じ素材が選択中素材にも
  // 入っている場合は素材側を残す (同一IDの二重計上で「衝突」扱いにしないため)。
  const seen = new Set(candidates.map((c) => c.target.id));
  for (const frame of sceneFrames) {
    if (seen.has(frame.target.id)) continue;
    seen.add(frame.target.id);
    candidates.push({ ...frame, badge: mentionBadgeLabel(idx) });
    idx += 1;
  }
  return candidates;
}

/** buildSceneFrameMentionCandidatesが読むシーンの最小構造 (UI版Scenario['scenes']と互換)。 */
export type SceneFrameMentionSceneLike = {
  id?: string;
  order: number;
  video_material_id?: string | null;
};

/** scene_frame_mentionsの値1件 (features/scenarios/types.ts SceneFrameMentionEntryと同形)。 */
export type SceneFrameMentionRecord = {
  video_material_id: string;
  scene_id: string;
  scene_order: number;
  position: 'first' | 'last';
  inserted_at?: string;
  touched_at?: string;
};

/**
 * generation_metadataからscene_frame_mentionsを取り出す。形が崩れたエントリ
 * (必須フィールド欠落・position不正)は捨てる。
 */
export function readSceneFrameMentionRecords(
  generationMetadata: Record<string, unknown> | null | undefined,
): Record<string, SceneFrameMentionRecord> {
  const raw = generationMetadata?.scene_frame_mentions;
  const result: Record<string, SceneFrameMentionRecord> = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return result;
  for (const [frameId, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!frameId || !value || typeof value !== 'object') continue;
    const v = value as Record<string, unknown>;
    const position = v.position;
    if (position !== 'first' && position !== 'last') continue;
    if (typeof v.video_material_id !== 'string' || !v.video_material_id) continue;
    if (typeof v.scene_id !== 'string' || !v.scene_id) continue;
    const order = Number(v.scene_order);
    if (!Number.isFinite(order)) continue;
    result[frameId] = {
      video_material_id: v.video_material_id,
      scene_id: v.scene_id,
      scene_order: order,
      position,
      inserted_at: typeof v.inserted_at === 'string' ? v.inserted_at : undefined,
      touched_at: typeof v.touched_at === 'string' ? v.touched_at : undefined,
    };
  }
  return result;
}

/** シーンフレーム候補のホバー名 (例: 「シーン1 · 最後（旧テイク）」)。 */
export function sceneFrameMentionLabel(info: Pick<SceneFrameMentionInfo, 'sceneOrder' | 'position' | 'stale' | 'missing'>): string {
  const base = `シーン${info.sceneOrder} · ${info.position === 'first' ? '最初' : '最後'}`;
  if (info.missing) return `${base}（参照先なし）`;
  if (info.stale) return `${base}（旧テイク）`;
  return base;
}

/**
 * 記録 (scene_frame_mentions)と現在のscenesから、チップ・警告用のシーンフレーム候補を
 * 再構成する (§2.2)。リロード後もこの記録から作れるので、既存トークンが生テキストに戻らない。
 *
 * - stale: 記録のvideo_material_id ≠ 参照元シーンの現在のvideo_material_id
 *   (シーンが無くなった場合もstale)。解決は選んだテイクのまま (固定)
 * - missing: options.missingFrameIdsに含まれる (GET .../frame-mentionsでフレーム素材が無い・
 *   削除済みと分かったもの)
 * - サムネイル: options.thumbnails (GET .../frame-mentions・挿入APIが返したフレーム素材自身の
 *   URL)だけを使う。シーンの入力フレーム (image_url・last_frame_url等)での代用はしない
 *   (参照している素材と別の画像になり得るため。特に最後のフレーム・旧テイク)。
 *   取れないときはサムネイル無し
 * - 並び: 挿入時刻 (inserted_at。再挿入では変わらない) → シーン番号 → 最初/最後。新しく挿入した
 *   フレームが常に末尾に付くので、挿入時に払い出したバッジ文字と再構成後のバッジ文字が揃う。
 *   バッジは仮の値 (buildMentionCandidatesが俳優→素材の後ろに付け直す)
 */
export function buildSceneFrameMentionCandidates(
  entries: Record<string, SceneFrameMentionRecord>,
  scenes: SceneFrameMentionSceneLike[],
  options: { thumbnails?: Record<string, string | null | undefined>; missingFrameIds?: Iterable<string> } = {},
): MentionCandidate[] {
  const missing = new Set(options.missingFrameIds ?? []);
  const sceneById = new Map<string, SceneFrameMentionSceneLike>();
  for (const scene of scenes) {
    if (scene.id) sceneById.set(scene.id, scene);
  }
  const rows = Object.entries(entries).sort(([, a], [, b]) => (
    (a.inserted_at ?? '').localeCompare(b.inserted_at ?? '')
    || a.scene_order - b.scene_order
    || (a.position === b.position ? 0 : a.position === 'first' ? -1 : 1)
  ));
  return rows.map(([frameId, entry], index) => {
    const scene = sceneById.get(entry.scene_id);
    const stale = (scene?.video_material_id || null) !== entry.video_material_id;
    const info: SceneFrameMentionInfo = {
      sceneId: entry.scene_id,
      sceneOrder: entry.scene_order,
      position: entry.position,
      videoMaterialId: entry.video_material_id,
      stale,
      missing: missing.has(frameId),
    };
    return {
      kind: 'scene_frame' as const,
      target: {
        id: frameId,
        thumbnailUrl: info.missing ? null : (options.thumbnails?.[frameId] || null),
        name: sceneFrameMentionLabel(info),
      },
      badge: mentionBadgeLabel(index),
      idHex4: mentionIdHex4(frameId),
      sceneFrame: info,
    };
  });
}

/**
 * 生成前警告用: textsのトークンのうち、シーンフレーム記録のid4に一致し、かつ
 * 「シーンフレーム記録 + otherIds (俳優・素材)」の中で異なるIDが2つ以上一致するもの
 * (= BEがどの経路でも解決しない衝突トークン)を返す (§2.0-3・§2.6)。
 * 衝突判定は「送られないのが正しい」除外より先に行う。
 */
export function collectSceneFrameMentionConflicts(
  texts: string[],
  sceneFrameIds: Iterable<string>,
  otherIds: Iterable<string>,
): UnresolvedMention[] {
  const frameIds = new Set(sceneFrameIds);
  const frameHex = new Set(Array.from(frameIds, mentionIdHex4));
  const idsByHex = new Map<string, Set<string>>();
  for (const id of [...frameIds, ...otherIds]) {
    if (!id) continue;
    const hex = mentionIdHex4(id);
    const set = idsByHex.get(hex) ?? new Set<string>();
    set.add(id);
    idsByHex.set(hex, set);
  }
  return texts.flatMap((text) => collectUnresolvedMentionsByCounter(text, (idHex4) => {
    if (!frameHex.has(idHex4)) return 1; // シーンフレームに関係しないトークンは対象外
    return idsByHex.get(idHex4)?.size ?? 0;
  })).filter((m) => m.reason === 'ambiguous');
}

/**
 * シーン動画の生成前警告 (useVideoHandlersのhandleGenerateSceneVideo)で出すトークンを求める。
 * - conflictTokens: シーンフレーム記録のid4が、記録・実送信IDの中で異なるIDに2つ以上一致する
 *   トークン (家系を問わず警告する)
 * - unresolvedTokens: 実送信ID・記録のどれにも一意一致しないトークン (skipUnresolvedなら空)。
 *   衝突トークンは重ねて出さない
 *
 * sentIdsは「実際に送信される素材ID (reference_material_ids・絵コンテ添付) + 俳優ID」。BEの動画経路の
 * 衝突判定 (references.py _scene_frame_mention_other_ids)と同じ集合にする。選択中素材全件は含めない
 * (送られない素材とのid4一致でBEは衝突扱いしないため、FEだけ警告するとずれる)。
 */
export function computeSceneVideoMentionWarnings(params: {
  texts: string[];
  sentIds: string[];
  generationMetadata: Record<string, unknown> | null | undefined;
  skipUnresolved: boolean;
}): { conflictTokens: string[]; unresolvedTokens: string[] } {
  const { texts, sentIds, generationMetadata, skipUnresolved } = params;
  const sceneFrameIds = Object.keys(readSceneFrameMentionRecords(generationMetadata));
  const conflictTokens = sceneFrameIds.length > 0
    ? Array.from(new Set(collectSceneFrameMentionConflicts(texts, sceneFrameIds, sentIds).map((m) => m.token)))
    : [];
  if (skipUnresolved) return { conflictTokens, unresolvedTokens: [] };
  const conflictSet = new Set(conflictTokens);
  const unresolvedTokens = Array.from(new Set(
    texts
      .flatMap((text) => collectUnresolvedMentionsForMaterialIds(text, [...sentIds, ...sceneFrameIds]))
      .filter((m) => !conflictSet.has(m.token))
      .map((m) => m.token),
  ));
  return { conflictTokens, unresolvedTokens };
}

/** トークン挿入書式: `@<バッジ文字>#<4hex>` (例: `@A#3f9c`)。 */
export function buildMentionToken(badge: string, assetId: string): string {
  return `@${badge}#${mentionIdHex4(assetId)}`;
}

// トークン正規表現: `@` + 英字1〜2文字 (バッジ文字) + `#` + 4hex ちょうど
// (直後に hex が続かないこと。6hex 続き等は無変換で残す)。
// ラベル部をバッジ文字書式に限定するのは、4 hex だけでは通常文章の偶然一致
// (`@売上#2024` の年号等) が現実味を持つため。
const MENTION_TOKEN_SOURCE = '@([A-Za-z]{1,2})#([0-9a-fA-F]{4})(?![0-9a-fA-F])';

export function createMentionTokenRegex(): RegExp {
  return new RegExp(MENTION_TOKEN_SOURCE, 'g');
}

/**
 * text 中で一意に解決できたメンショントークン1件分の範囲情報。
 * トークンのチップ表示 (docs/features/material-at-mention-plan.md「トークンのチップ表示」節)
 * が、本文の `@A#3f9c` の上に「サムネイル+バッジ」を重ねる位置を決めるために使う。
 */
export type MentionChipRange = {
  /** トークン開始 index (`@` の位置)。 */
  start: number;
  /** トークン終了 index (排他的)。 */
  end: number;
  /**
   * トークン本文に実際に書かれている英字部分 (バッジ払い出し直後の文字ではなく、
   * regex がその場でキャプチャした文字そのもの)。チップ非表示時 (選択範囲がトークンと
   * 交差した場合等) に見える生テキストと表示を一致させるため、payout し直さない。
   */
  label: string;
  /** 一意に解決された候補。 */
  candidate: MentionCandidate;
};

/**
 * text 中のメンショントークンを走査し、4hex の ID 部が candidates 集合に
 * ちょうど1件一致するもの (＝生成時にも一意に解決される) だけをチップ化対象として返す。
 * 0件 (未選択素材) ・2件以上 (衝突) はバックエンドの「複数一致は決して解決しない」
 * 規約と表示を一致させるため、あえてチップ化しない (生テキストのまま見せる)。
 */
export function computeMentionChipRanges(text: string, candidates: MentionCandidate[]): MentionChipRange[] {
  const ranges: MentionChipRange[] = [];
  if (!text) return ranges;
  const regex = createMentionTokenRegex();
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const [token, badge, hex] = match;
    const idHex4 = hex.toLowerCase();
    const matches = candidates.filter((c) => c.idHex4 === idHex4);
    if (matches.length !== 1) continue;
    ranges.push({
      start: match.index,
      end: match.index + token.length,
      label: badge,
      candidate: matches[0],
    });
  }
  return ranges;
}

/**
 * text 分割の1要素。'text' は通常テキスト (連結すれば text と一致する)、'token' は
 * computeMentionChipRanges が返した1件分のチップ化対象トークン。
 * MentionTokenInlineText (読み取り表示のインライン置換。
 * docs/features/material-at-mention-plan.md「トークンのチップ表示」節) が、
 * ranges の隙間を通常テキストとして復元しつつトークン部分だけチップ要素に
 * 差し替えるために使う。
 */
export type MentionTextSegment =
  | { type: 'text'; value: string }
  | { type: 'token'; range: MentionChipRange };

/**
 * text を ranges (start でソート済み前提。computeMentionChipRanges の返り値は
 * 走査順＝start昇順なのでそのまま渡せる) で分割する。range 間・先頭・末尾の
 * 通常テキストが欠落/重複しないよう、必ず cursor を range.end まで進めてから
 * 次に移る (range.start > cursor のときだけ通常テキスト segment を挟む)。
 */
export function splitTextByMentionRanges(
  text: string,
  ranges: MentionChipRange[],
): MentionTextSegment[] {
  const segments: MentionTextSegment[] = [];
  let cursor = 0;
  for (const range of ranges) {
    if (range.start > cursor) {
      segments.push({ type: 'text', value: text.slice(cursor, range.start) });
    }
    segments.push({ type: 'token', range });
    cursor = range.end;
  }
  if (cursor < text.length) {
    segments.push({ type: 'text', value: text.slice(cursor) });
  }
  return segments;
}

export type UnresolvedMentionReason = 'not_found' | 'ambiguous';

export type UnresolvedMention = {
  /** 一致したトークン全体 (例: `@A#3f9c`)。 */
  token: string;
  /** バッジ部 (表示専用、判定には使わない)。 */
  badge: string;
  /** ID 部 (小文字正規化済み)。 */
  idHex4: string;
  /** text 内でのマッチ開始位置。 */
  index: number;
  /** 'not_found' = 候補内に一致なし / 'ambiguous' = 候補内に複数一致。 */
  reason: UnresolvedMentionReason;
};

/**
 * text 中のメンショントークンを走査し、各トークンについて `countMatches(idHex4)`
 * (候補集合内で何件一致したか) を求め、1件ちょうど一致しないもの (不在・複数一致とも)
 * を UnresolvedMention として集める共通実装。collectUnresolvedMentions /
 * collectUnresolvedMentionsForMaterialIds の重複を避けるための内部ヘルパー。
 */
function collectUnresolvedMentionsByCounter(
  text: string,
  countMatches: (idHex4: string) => number,
): UnresolvedMention[] {
  const results: UnresolvedMention[] = [];
  if (!text) return results;
  const regex = createMentionTokenRegex();
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const [token, badge, hex] = match;
    const idHex4 = hex.toLowerCase();
    const matchCount = countMatches(idHex4);
    if (matchCount !== 1) {
      results.push({
        token,
        badge,
        idHex4,
        index: match.index,
        reason: matchCount === 0 ? 'not_found' : 'ambiguous',
      });
    }
  }
  return results;
}

/**
 * text 中のメンショントークンをスキャンし、4hex の ID 部が candidates 集合に
 * 一意一致しないもの (不在・複数一致とも) を返す。生成は止めず警告のみに使う
 * ことを想定した副作用のない純関数 (docs/features/material-at-mention-plan.md
 * P2「生成前の未解決トークン警告」)。
 */
export function collectUnresolvedMentions(
  text: string,
  candidates: MentionCandidate[],
): UnresolvedMention[] {
  return collectUnresolvedMentionsByCounter(
    text,
    (idHex4) => candidates.reduce((count, c) => (c.idHex4 === idHex4 ? count + 1 : count), 0),
  );
}

/**
 * collectUnresolvedMentions の ID 集合版。候補を MentionCandidate (バッジ文字付き)
 * ではなく素材 ID の集合だけで受け取る。
 *
 * 生成前警告は本来「実際に送信される参照素材」を候補とすべきだが、動画モデルの
 * 参照上限 (buildReferenceList での切り詰め・俳優のキャラクターシート枠消費を含む)
 * により、selectedAssets 全体 (isMentionableReferenceAsset) より狭い集合になる
 * ことがある (docs/features/material-at-mention-plan.md レビュー指摘 P1)。
 * バッジ文字は警告文言に使わないため、ここでは ID だけで判定できれば十分。
 */
export function collectUnresolvedMentionsForMaterialIds(
  text: string,
  materialIds: Iterable<string>,
): UnresolvedMention[] {
  const idHex4List = Array.from(materialIds, mentionIdHex4);
  return collectUnresolvedMentionsByCounter(
    text,
    (idHex4) => idHex4List.reduce((count, h) => (h === idHex4 ? count + 1 : count), 0),
  );
}
